// modules/elearning/documents/DocumentFilter.jsx
import React, { useState, useEffect } from "react";
import { 
  Search, 
  RotateCw, 
  SlidersHorizontal, 
  Plus, 
  MoreVertical, 
  ChevronDown,
  Eraser,
  FileText
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const DocumentFilter = ({ onNewClick, onFilterChange }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [filters, setFilters] = useState({
    keyword: "",
    subject: "",
    type: "",
    extension: "",
    is_active: "",
  });
  
  // ✅ Lấy options "Môn học" từ API thay vì hardcode
  useEffect(() => {
    let isMounted = true;
    setSubjectsLoading(true);
    getSubjectsLookup()
      .then((res) => {
        const items = res?.items || res;
        const mapped = (Array.isArray(items) ? items : []).map((s) => ({
          value: s.code,
          label: s.name,
        }));
        if (isMounted) setSubjectOptions(mapped);
      })
      .catch((err) => {
        console.error("Lỗi khi lấy môn học:", err);
        if (isMounted) setSubjectOptions([]);
      })
      .finally(() => {
        if (isMounted) setSubjectsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const documentTypes = [
    { label: "Giáo trình", value: "giaotrinh" },
    { label: "Bài giảng", value: "baigiang" },
    { label: "Bài tập", value: "baitap" },
    { label: "Video bài học", value: "video" },
    { label: "Đề kiểm tra", value: "dethi" },
    { label: "Khác", value: "khac" },
  ];

  const extOptions = [
    { value: "zip", label: "zip" },
    { value: "pdf", label: "pdf" },
    { value: "mp4", label: "video" },
  ];

  const statusOptions = [
    { value: "1", label: "Hoạt động" },
    { value: "0", label: "Tạm ngưng" },
  ];

  const isFiltering = Object.values(filters).some((v) => v !== "");

  // ✅ Responsive detector
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Debounce tìm kiếm
  useEffect(() => {
    const timer = setTimeout(() => onFilterChange?.(filters), 400);
    return () => clearTimeout(timer);
  }, [filters.keyword]);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = { keyword: "", subject: "", type: "", extension: "", is_active: "" };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  // ================= RENDER COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active 
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm" 
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 pr-10 rounded-xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors" />
      </div>
    </div>
  );

  // ================= MOBILE VIEW =================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2 text-left">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm tên tài liệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} />
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc tài liệu"
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
            >
              ÁP DỤNG
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox label="Môn học" value={filters.subject} options={subjectOptions} onChange={(v) => updateFilter("subject", v)} />
            <SelectBox label="Loại" value={filters.type} options={documentTypes} onChange={(v) => updateFilter("type", v)} />
            <SelectBox label="Định dạng" value={filters.extension} options={extOptions} onChange={(v) => updateFilter("extension", v)} />
            <SelectBox label="Trạng thái" value={filters.is_active} options={statusOptions} onChange={(v) => updateFilter("is_active", v)} />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ================= DESKTOP VIEW =================
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6 text-left">
      <div className="flex items-center justify-between gap-4">
        {/* Nhóm tìm kiếm & nút chức năng */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm theo tên tài liệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={() => clearFilters()} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />
          
          {isFiltering && (
             <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
                <Eraser size={14} /> Xóa lọc
             </button>
          )}
        </div>

        {/* Nút Thêm mới */}
        {hasPermission("elearning.documents", "C") && (
          <button
            onClick={onNewClick}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
          >
            <Plus size={18} />
            Thêm tài liệu
          </button>
        )}
      </div>

      {/* Vùng lọc nâng cao */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox label="Môn học" value={filters.subject} options={subjectOptions} onChange={(v) => updateFilter("subject", v)} />
              <SelectBox label="Loại tài liệu" value={filters.type} options={documentTypes} onChange={(v) => updateFilter("type", v)} />
              <SelectBox label="Định dạng" value={filters.extension} options={extOptions} onChange={(v) => updateFilter("extension", v)} />
              <SelectBox label="Trạng thái" value={filters.is_active} options={statusOptions} onChange={(v) => updateFilter("is_active", v)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentFilter;